import React, { useState, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiInstagram,
  FiFacebook,
} from "react-icons/fi";
import { FaTiktok, FaPinterest } from "react-icons/fa";

const ContactSection = styled.section`
  padding: 60px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 40px 15px;
    min-height: auto;
  }

  @media (max-width: 480px) {
    padding: 30px 12px;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(142, 116, 121, 0.2),
      transparent
    );
  }
`;

const Container = styled(motion.div)`
  max-width: 1400px;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  background: rgba(43, 40, 40, 0.5);
  backdrop-filter: blur(20px);
  border-radius: 60px;
  padding: 60px;
  box-shadow:
    0 30px 60px rgba(99, 86, 78, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(155, 128, 103, 0.1);

  @media (max-width: 1200px) {
    gap: 60px;
    padding: 50px;
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 50px;
    padding: 40px;
    border-radius: 40px;
  }

  @media (max-width: 768px) {
    padding: 35px 25px;
    border-radius: 30px;
    gap: 40px;
  }

  @media (max-width: 480px) {
    padding: 30px 20px;
    border-radius: 25px;
    gap: 35px;
    backdrop-filter: blur(10px);
  }
`;

// Left Side
const LeftSide = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 40px;
  border-right: 1px solid rgba(155, 128, 103, 0.2);

  @media (max-width: 968px) {
    border-right: none;
    padding-right: 0;
    text-align: center;
  }
`;

const Title = styled(motion.h2)`
  font-family: "Lovelace", serif;
  font-size: clamp(2.5rem, 4vw, 4rem);
  color: #ffffff;
  margin: 0 0 20px;
  font-weight: 400;
  letter-spacing: -1px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    margin-bottom: 12px;
  }
`;

const Subtitle = styled(motion.p)`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.3rem;
  color: #cfc0c3;
  font-style: italic;
  margin: 0 0 40px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 30px;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 25px;
    line-height: 1.5;
  }
`;

const ContactInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 40px 0;

  @media (max-width: 768px) {
    gap: 20px;
    margin: 30px 0;
    align-items: center;
  }

  @media (max-width: 480px) {
    gap: 15px;
    margin: 25px 0;
  }
`;

const InfoItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 15px;
  color: #c5b8af;

  svg {
    color: #9b8067;
    font-size: 1.3rem;
    flex-shrink: 0;

    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }

  span {
    font-size: 1rem;
    line-height: 1.4;

    @media (max-width: 768px) {
      font-size: 0.95rem;
      text-align: left;
    }

    @media (max-width: 480px) {
      font-size: 0.9rem;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 400px;
    justify-content: flex-start;
  }
`;

const SocialSection = styled(motion.div)`
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 15px;
  }
`;

const SocialTitle = styled.h4`
  font-family: "Lovelace", serif;
  font-size: 1.2rem;
  color: #c5b9b2;
  margin: 0 0 20px;
  font-weight: 400;
  letter-spacing: 1px;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 12px;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 15px;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    flex-wrap: wrap;
  }
`;

const SocialIcon = styled(motion.a)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9b8067;
  font-size: 1.3rem;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(155, 128, 103, 0.1);
  border: 1px solid rgba(155, 128, 103, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: #9b8067;
    color: white;
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
  }
`;

// Right Side
const RightSide = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const FormWrapper = styled.div`
  width: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 768px) {
    gap: 25px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }
`;

const InputGroup = styled(motion.div)`
  position: relative;
  width: 100%;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 15px 0;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(155, 128, 103, 0.2);
  font-family: "Cormorant Garamond", serif;
  font-size: 1.2rem;
  color: #ffffff;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-bottom: 1px solid #9b8067;
  }

  &::placeholder {
    color: rgba(243, 235, 236, 0.4);
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 1px;
    font-family: "Montserrat", sans-serif;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 12px 0;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 10px 0;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 15px 0;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(155, 128, 103, 0.2);
  font-family: "Cormorant Garamond", serif;
  font-size: 1.2rem;
  color: #e9e4e1;
  outline: none;
  resize: vertical;

  &:focus {
    border-bottom: 1px solid #9b8067;
  }

  &::placeholder {
    color: rgba(212, 204, 206, 0.4);
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 1px;
    font-family: "Montserrat", sans-serif;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    min-height: 100px;
    padding: 12px 0;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    min-height: 90px;
    padding: 10px 0;
  }
`;

const SubmitButton = styled(motion.button)`
  align-self: flex-start;
  padding: 16px 40px;
  background: #9b8067;
  color: white;
  border: none;
  font-family: "Montserrat", sans-serif;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 0.8rem;
  cursor: pointer;
  margin-top: 20px;
  box-shadow: 0 10px 20px rgba(155, 128, 103, 0.2);

  &:disabled {
    background: #d4c4bc;
    box-shadow: none;
    cursor: not-allowed;
  }

  @media (max-width: 968px) {
    align-self: center;
  }

  @media (max-width: 768px) {
    padding: 14px 35px;
    font-size: 0.75rem;
    margin-top: 15px;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 14px 20px;
    font-size: 0.7rem;
    letter-spacing: 1.5px;
  }
`;

const SuccessMessage = styled(motion.div)`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;

  p {
    font-family: "Lovelace", serif;
    font-size: 2rem;
    color: #63564e;
    margin: 20px 0;
    line-height: 1.4;
  }

  @media (max-width: 768px) {
    min-height: 300px;

    p {
      font-size: 1.5rem;
      margin: 15px 0;
    }
  }

  @media (max-width: 480px) {
    min-height: 250px;

    p {
      font-size: 1.2rem;
      margin: 12px 0;
    }
  }
`;

const SuccessIcon = styled(motion.div)`
  width: 100px;
  height: 100px;
  background: #9b8067;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    font-size: 2rem;
    margin-bottom: 25px;
  }

  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
    margin-bottom: 20px;
  }
`;

// --- Animation Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  },
};

// --- Component ---
const Contact = () => {
  const form = useRef();
  const [isSending, setIsSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSending(true);

    emailjs
      .sendForm(
        "service_trapati",
        "template_xjsbt0r",
        form.current,
        "blCFi7ZKWMG7tDapf",
      )
      .then(
        () => {
          setSubmitted(true);
          setIsSending(false);
        },
        () => {
          alert("Something went wrong. Please try again.");
          setIsSending(false);
        },
      );
  };

  const socialLinks = [
    { icon: <FiInstagram />, url: "https://instagram.com", label: "Instagram" },
    { icon: <FiFacebook />, url: "https://facebook.com", label: "Facebook" },
    { icon: <FaTiktok />, url: "https://tiktok.com", label: "TikTok" },
    { icon: <FaPinterest />, url: "https://pinterest.com", label: "Pinterest" },
  ];

  return (
    <ContactSection id="contact">
      <Container
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <AnimatePresence mode="wait">
          {!submitted ? (
            <>
              {/* Left Side */}
              <LeftSide variants={itemVariants}>
                <Title variants={itemVariants}>
                  Ready to Capture Your Story?
                </Title>

                <Subtitle variants={itemVariants}>
                  I’d love to hear about your vision and create something
                  timeless together. Send me a message and I’ll get back to you
                  as soon as possible.
                </Subtitle>

                <ContactInfo>
                  <InfoItem variants={itemVariants}>
                    <FiMail />
                    <span>info@yourstudio.com</span>
                  </InfoItem>

                  <InfoItem variants={itemVariants}>
                    <FiPhone />
                    <span>+1 234 567 890</span>
                  </InfoItem>

                  <InfoItem variants={itemVariants}>
                    <FiMapPin />
                    <span>
                      123 Studio Street,
                      <br /> New York, NY 10001
                    </span>
                  </InfoItem>
                </ContactInfo>

                <SocialSection variants={itemVariants}>
                  <SocialTitle>Follow My Work</SocialTitle>

                  <SocialIcons>
                    {socialLinks.map((social, index) => (
                      <SocialIcon
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label={social.label}
                      >
                        {social.icon}
                      </SocialIcon>
                    ))}
                  </SocialIcons>
                </SocialSection>
              </LeftSide>

              {/* Right Side - Form */}
              <RightSide variants={itemVariants}>
                <FormWrapper>
                  <Form ref={form} onSubmit={sendEmail}>
                    <InputGroup variants={itemVariants}>
                      <StyledInput
                        type="text"
                        name="name"
                        placeholder="YOUR NAME"
                        required
                      />
                    </InputGroup>

                    <InputGroup variants={itemVariants}>
                      <StyledInput
                        type="email"
                        name="email"
                        placeholder="YOUR EMAIL"
                        required
                      />
                    </InputGroup>

                    <InputGroup variants={itemVariants}>
                      <StyledInput
                        type="phone"
                        name="phone"
                        placeholder="PHONE NUMBER"
                        required
                      />
                    </InputGroup>

                    <InputGroup variants={itemVariants}>
                      <StyledTextArea
                        name="message"
                        placeholder="TELL ME ABOUT YOUR PROJECT"
                        required
                      />
                    </InputGroup>

                    <SubmitButton
                      variants={itemVariants}
                      disabled={isSending}
                      whileHover={{ scale: 1.05, backgroundColor: "#8e7479" }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                    >
                      {isSending ? "Sending..." : "Send Message"}
                    </SubmitButton>
                  </Form>
                </FormWrapper>
              </RightSide>
            </>
          ) : (
            <SuccessMessage
              key="success"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: "spring", damping: 15 }}
            >
              <SuccessIcon
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", damping: 12 }}
              >
                📸
              </SuccessIcon>

              <p>
                Thank You!
                <br />
                I’ll get back to you very soon.
              </p>

              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{ color: "#9b8067" }}
              >
                Your Photographer
              </motion.span>
            </SuccessMessage>
          )}
        </AnimatePresence>
      </Container>
    </ContactSection>
  );
};

export default Contact;
